import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { InferenceRequest } from "../types/inference-request";
import { getInferenceBaseUrl } from "../../../config/config";
import { sec } from "../../../utils/security";
import { InferenceResponse } from "../types/inference-response";

/**
 * Tags an inference request as originating from the Word Add-In so the backend
 * attributes billing to the Word Add-In resource type. The tag is authoritative
 * (applied after the caller's fields). (Ticket 237)
 */
export const withWordAddInSource = (request: InferenceRequest): InferenceRequest => ({
  ...request,
  source: "word-addin",
});

export const inferenceApi = createApi({
  reducerPath: "inferenceApi",
  baseQuery: async (args, api, extraOptions) =>
    fetchBaseQuery({
      baseUrl: getInferenceBaseUrl(),
      prepareHeaders: async (headers) => {
        const token = await sec.getAccessTokenSilently()();
        if (token) headers.set("Authorization", `Bearer ${token}`);
        return headers;
      },
    })(args, api, extraOptions),

  endpoints: (builder) => ({
    runInference: builder.mutation<
      InferenceResponse,
      { modelObjectIdentifier: string; request: InferenceRequest }
    >({
      query: ({ modelObjectIdentifier, request }) => ({
        url: `inference/completions/${modelObjectIdentifier}`,
        method: "POST",
        // Tag every add-in inference call as the Word Add-In so billing is
        // attributed to that resource type in analytics. (Ticket 237)
        body: withWordAddInSource(request),
      }),
    }),
  }),
});

export const { useRunInferenceMutation } = inferenceApi;
